/*
 * Complete the Table (October 2026), product pages only.
 *
 * What this does:
 * 1. Table page: when the size changes, the seat number in the lines and the
 *    chair picker follows the chosen size (seat map comes from Liquid, from
 *    the same variant metafield the discount was built from).
 * 2. Table page: "Add chairs to cart" posts the chosen chair and quantity to
 *    the cart and tells the cart drawer, the same way the card quick add does.
 * 3. Any page with a live block: after every cart change it fetches the
 *    server-rendered helper (/?section_id=teak-ctt-state), so the text comes
 *    from the same Liquid as the cart drawer. If that fetch fails, the block
 *    is hidden rather than left stale.
 *
 * What this never does: work out what Shopify charges. The cart helper reads
 * Shopify's own discount lines. The only arithmetic here is "seats x biggest
 * chair saving" for the "save up to" line, using numbers rendered by Liquid.
 */
import { CartAddEvent } from '@theme/events';

(function () {
  if (window.__TeakCttInit) return;
  window.__TeakCttInit = true;

  const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function themeRoutes() {
    if (typeof Theme !== 'undefined' && Theme.routes) return Theme.routes;
    return (window.Theme && window.Theme.routes) || {};
  }

  function money(cents) {
    const dollars = cents / 100;
    const whole = Number.isInteger(dollars);
    return '$' + dollars.toLocaleString('en-AU', {
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: 2,
    });
  }

  function parseMap(el) {
    try {
      return JSON.parse(el.getAttribute('data-ctt-seats-map') || '{}');
    } catch (e) {
      return {};
    }
  }

  function cartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId && !ids.includes(item.dataset.sectionId)) {
        ids.push(item.dataset.sectionId);
      }
    });
    return ids;
  }

  /* ---------- 1. Seat number follows the chosen size ---------- */

  function applySeats(el, seats) {
    el.hidden = !(seats > 0);
    el.querySelectorAll('[data-ctt-seats-n]').forEach((n) => {
      n.textContent = String(seats);
    });
    const maxOff = Number(el.getAttribute('data-ctt-max-off')) || 0;
    const upto = el.querySelector('[data-ctt-save-upto]');
    if (upto && maxOff > 0) upto.textContent = money(maxOff * seats);
    const qty = el.querySelector('[data-ctt-qty]');
    if (qty && qty.dataset.touched !== 'true' && seats > 0) {
      qty.value = String(seats);
      updateAddLabel(el);
    }
  }

  function bindSeatFollow(el) {
    const map = parseMap(el);
    const pshow = el.closest('[id^="teak-pshow-"]');
    if (!pshow) return;
    pshow.addEventListener('teak:variant-applied', (event) => {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;
      applySeats(el, Number(map[String(variant.id)]) || 0);
    });
  }

  /* ---------- Chair page: deal price follows the variant ---------- */

  function bindChairPrice(el) {
    const pct = Number(el.getAttribute('data-ctt-pct')) || 0;
    const priceEl = el.querySelector('[data-ctt-chair-price]');
    const saveEl = el.querySelector('[data-ctt-chair-save]');
    const pshow = el.closest('[id^="teak-pshow-"]');
    if (!pct || !pshow || !(priceEl || saveEl)) return;
    pshow.addEventListener('teak:variant-applied', (event) => {
      const variant = event.detail && event.detail.variant;
      if (!variant || typeof variant.price !== 'number') return;
      const off = Math.round((variant.price * pct) / 100);
      if (priceEl) priceEl.textContent = money(variant.price - off);
      if (saveEl) saveEl.textContent = money(off);
    });
  }

  /* ---------- 2. Add chairs ---------- */

  function updateAddLabel(panel) {
    const qty = panel.querySelector('[data-ctt-qty]');
    const label = panel.querySelector('[data-ctt-add-label]');
    if (!qty || !label) return;
    const n = Math.max(1, Number(qty.value) || 1);
    label.textContent = n === 1 ? 'Add 1 chair to cart' : 'Add ' + n + ' chairs to cart';
  }

  function bindPicker(panel) {
    const qty = panel.querySelector('[data-ctt-qty]');
    const down = panel.querySelector('[data-ctt-qty-down]');
    const up = panel.querySelector('[data-ctt-qty-up]');
    const add = panel.querySelector('[data-ctt-add]');
    const select = panel.querySelector('[data-ctt-chair-select]');
    const error = panel.querySelector('[data-ctt-error]');
    if (!qty || !add || !select) return;

    const clamp = (v) => Math.min(60, Math.max(1, Number(v) || 1));
    const touch = () => {
      qty.dataset.touched = 'true';
      updateAddLabel(panel);
    };
    down && down.addEventListener('click', () => { qty.value = String(clamp(Number(qty.value) - 1)); touch(); });
    up && up.addEventListener('click', () => { qty.value = String(clamp(Number(qty.value) + 1)); touch(); });
    qty.addEventListener('change', () => { qty.value = String(clamp(qty.value)); touch(); });
    updateAddLabel(panel);

    add.addEventListener('click', async () => {
      const routes = themeRoutes();
      const variantId = Number(select.value);
      const option = select.options[select.selectedIndex];
      const quantity = clamp(qty.value);
      if (!routes.cart_add_url || !variantId || add.disabled) return;

      add.disabled = true;
      if (error) error.hidden = true;
      const label = panel.querySelector('[data-ctt-add-label]');
      if (label) label.textContent = 'Adding...';

      try {
        const sections = cartSectionIds();
        const response = await fetch(routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity }],
            sections: sections.join(','),
            sections_url: window.location.pathname,
          }),
        }).then((res) => res.json());

        if (response.status) {
          throw new Error(response.description || response.message || 'We could not add the chairs.');
        }

        const cart = await fetch(rootUrl() + 'cart.js').then((res) => res.json());
        document.dispatchEvent(
          new CartAddEvent(cart, String(variantId), {
            source: 'teak-ctt',
            productId: option ? option.getAttribute('data-product-id') : undefined,
            variantId: String(variantId),
            itemCount: (cart && cart.item_count) || quantity,
            sections: response.sections || {},
          })
        );
        if (label) label.textContent = 'Added';
        window.setTimeout(() => updateAddLabel(panel), 1600);
      } catch (e) {
        console.error(e);
        if (error) {
          error.textContent = (e && e.message) || 'We could not add the chairs. Please try again.';
          error.hidden = false;
        }
        updateAddLabel(panel);
      } finally {
        add.disabled = false;
      }
    });
  }

  /* ---------- 3. Live cart state ---------- */

  let refreshing = null;
  function refreshLive() {
    const blocks = document.querySelectorAll('[data-ctt-live]');
    if (!blocks.length) return;
    if (refreshing) return refreshing;
    refreshing = fetch(rootUrl() + '?section_id=teak-ctt-state', { headers: { Accept: 'text/html' } })
      .then((res) => {
        if (!res.ok) throw new Error('status ' + res.status);
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-ctt-cart]');
        blocks.forEach((block) => {
          block.innerHTML = fresh ? fresh.outerHTML : '';
          block.hidden = !fresh;
        });
      })
      .catch(() => {
        blocks.forEach((block) => {
          block.innerHTML = '';
          block.hidden = true;
        });
      })
      .finally(() => {
        refreshing = null;
      });
    return refreshing;
  }

  function init() {
    document.querySelectorAll('[data-ctt-table-line], [data-ctt-table-panel]').forEach(bindSeatFollow);
    document.querySelectorAll('[data-ctt-chair-line]').forEach(bindChairPrice);
    document.querySelectorAll('[data-ctt-table-panel]').forEach(bindPicker);
    document.addEventListener('cart:update', () => {
      // Let Shopify finish applying automatic discounts before reading.
      window.setTimeout(refreshLive, 150);
    });
    // The page itself may come from a cache; read the cart once on load too.
    refreshLive();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
